import { getMedias } from "@omni/mappers/product/shared/media.mapper";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { Media } from "@omni/types/api/shared/bff/media.model";
import { ModelMapper } from "@omni/types/utils/modelMapper";
import { pick } from "lodash-es";

import { translateWithoutContext } from "@/redux/modules/value/translations/translations.helpers";
import { CustomerOrderApiModel } from "@/types/service/cart/orders/customer/customerOrder.api.model";
import { CustomerOrderItemApiModel } from "@/types/service/cart/orders/customer/customerOrderItem.api.model";
import {
    ArticleConfiguration,
    OrderLineStatus,
    TransportPackageStatus
} from "@/types/service/cart/shared";
import { Config } from "@/utils/config.utils";
import { productConfigurations } from "@/utils/productConfiguration/productConfiguration.utils";

export type AdditionalServiceOrderProduct = {
    sku: string;
    title: string;
    price?: number;
    id: string;
};

export type CustomerOrderItemModel = {
    sku: string;
    title?: string;
    url?: string | null;
    medias?: Array<Media> | null;
    warrantyDeposit: boolean;
    articleConfiguration?: ArticleConfiguration | null;
    status: Array<OrderLineStatus>;
    transportPackageStatuses: Array<TransportPackageStatus>;
    deliveryType: DeliveryType;
    quantity: number;
    totalPrice: number;
    configuration: Record<string, unknown>;
    additionalServices: Array<AdditionalServiceOrderProduct>;
    isAdditionalService: boolean;
};

export type CustomerOrderModel = {
    id: string;
    creationDate: string;
    totalPrice: number;
    items: Array<CustomerOrderItemModel>;
};

export interface CustomerOrderListResponse {
    errors: Array<string>;
    warnings: Array<string>;
    totalPages: number;
    orders: Array<CustomerOrderApiModel>;
    pageSize: number;
    currentPage: number;
    totalElements: number;
}

export interface CustomerOrderListModel {
    totalElements: number;
    orders: Array<CustomerOrderModel>;
}

const getTitle = (model: CustomerOrderItemApiModel) => {
    const name = model.name;
    const brandName = model.brandName;

    if (model.warrantyDeposit) {
        return translateWithoutContext("general.product.pallet.title");
    }

    if (model.articleConfiguration) {
        return model.articleConfiguration.title || "";
    }

    return brandName ? `${brandName} ${name}` : name || "";
};

const getUrl = (model: CustomerOrderItemApiModel) => {
    if (model.giftCardArticle) {
        return Config.pages.giftcard;
    } else if (model.articleConfiguration) {
        return undefined;
    }

    return model.productUrl;
};

const isAdditionalServiceItem = (
    items: CustomerOrderItemApiModel[],
    id?: string
) => {
    return (
        !!id &&
        items.some((i) => i.additionalServices?.map((as) => as.id).includes(id))
    );
};

const CustomerOrderItemMapper: (
    items: CustomerOrderItemApiModel[]
) => ModelMapper<CustomerOrderItemApiModel, CustomerOrderItemModel> =
    (items) => (model) => {
        return {
            sku: model.sku,
            warrantyDeposit: model.warrantyDeposit,
            title: getTitle(model),
            url: getUrl(model),
            medias: getMedias(
                model.warrantyDeposit,
                getTitle(model),
                model.mainImages,
                model.articleConfiguration
            ),
            articleConfiguration: model.articleConfiguration,
            deliveryType: model.deliveryType as DeliveryType,
            transportPackageStatuses: model.transportPackageStatuses || [],
            quantity: model.quantity,
            status: model.orderLineStatuses || [],
            totalPrice: model.totalPrice,
            configuration: pick(
                model,
                productConfigurations.reduce(
                    (acc, pc) => [...acc, ...pc.configPaths],
                    [] as string[]
                )
            ),
            additionalServices: model.additionalServices,
            isAdditionalService: isAdditionalServiceItem(items, model.id)
        };
    };

const CustomerOrderMapper: ModelMapper<
    CustomerOrderApiModel,
    CustomerOrderModel
> = (model) => {
    return {
        creationDate: model.creationDate,
        id: model.id,
        totalPrice: model.totalPrice,
        items: model.items.map((i) => CustomerOrderItemMapper(model.items)(i))
    };
};

export const CustomerOrderListMapper: ModelMapper<
    CustomerOrderListResponse,
    CustomerOrderListModel
> = (model) => {
    const output = model?.orders?.map(CustomerOrderMapper) || [];
    return {
        totalElements: model.totalElements,
        orders: output
    };
};
